import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import {
  formatDateToWeekdayString,
  formatDDay,
  CloseButton,
  BaseButton,
  Chip,
  Toast,
  useAuth,
  useCopyToast,
  useIsMobile,
  useT,
} from '@repo/common';

import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/24px/icon_24px_copy.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/20px/icon_20px_download.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import RefundModal from '../../../Kpathport/Modal/RefundModal';
import { getWorkshopDetail } from '../../../../api/workshop/workshop.api';

const LABEL_KR = {
  Waitlist: '대기',
  Upcoming: '예정',
  Attended: '참석',
  Absent: '불참',
};

const LABEL_TONE = {
  Upcoming: { bg: 'bg-purple-purple01', text: 'text-text-accent' },
  Attended: { bg: 'bg-green-light02', text: 'text-green-deep04' },
};
const DEFAULT_LABEL_TONE = {
  bg: 'bg-grayscale-gray02',
  text: 'text-text-placeholder',
};

const BookingDetailModal = ({ data, onClose, label }) => {
  const t = useT();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { copy, copyToast } = useCopyToast();
  const [workshopInfo, setWorkshopInfo] = useState(null);
  const [infoModal, setInfoModal] = useState(null); // 'thingsToKnow' | 'refundPolicy'

  const openInfoModal = async (type) => {
    setInfoModal(type);
    if (workshopInfo || !data?.workshopId) return;
    try {
      const detail = await getWorkshopDetail(data.workshopId);
      setWorkshopInfo({
        thingsToKnow: detail?.thingsToKnow || '',
        refundPolicy: detail?.refundPolicy || '',
      });
    } catch (err) {
      console.error('Failed to load workshop info:', err);
      setWorkshopInfo({ thingsToKnow: '', refundPolicy: '' });
    }
  };

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    const originalPadding = window.getComputedStyle(document.body).paddingRight;

    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    if (scrollBarWidth > 0) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.paddingRight = originalPadding;
    };
  }, []);

  const labelTone = LABEL_TONE[label] ?? DEFAULT_LABEL_TONE;
  const formatTime = (time) => time?.slice(0, 5);

  const handleCopy = () => copy(data.bookingSerialNumber);

  const handleDownloadReceipt = () => {
    // 계좌이체·무료 예약은 PG 영수증이 없다 — null 로 새 탭을 열지 않는다
    if (data?.receiptUrl) window.open(data.receiptUrl, '_blank');
  };

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 z-[10000000] flex bg-surface-black40 ${
        isMobile ? '' : 'items-center justify-center'
      }`}
    >
      <div
        className='absolute inset-0'
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />

      <div
        className={`relative flex flex-col bg-grayscale-gray01 overflow-clip ${
          isMobile
            ? 'w-full h-full max-w-none max-h-none rounded-none'
            : 'w-full max-w-[700px] h-full max-h-[700px] rounded-[30px]'
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className='w-full flex flex-row-reverse tabletH:flex-row items-center justify-between pt-6 pb-[18px] px-[30px] bg-grayscale-gray01 border-b-grayscale-gray02 border-b'>
          <div className='w-6 h-6 shrink-0' />
          <div className='typo-b16Strong text-text-primary'>
            {t('Booking detail', '예약 상세')}
          </div>
          <CloseButton type='default' onClick={onClose} />
        </div>

        <div
          className={`w-full px-4 pt-6 tabletH:p-[30px] flex-1 min-h-0 ${
            label === 'Attended' ? 'pb-[30px]' : 'pb-[60px]'
          } flex flex-col items-start border-t border-t-grayscale-gray02 bg-grayscale-gray01 overflow-y-auto`}
        >
          <div className='flex flex-col items-start tabletH:gap-2 pb-4 tabletH:pb-6 w-full'>
            {label === 'Attended' && (
              <div className='flex gap-0.5 typo-b13 text-grayscale-gray04'>
                <InfoIcon className='text-grayscale-gray03' />
                {t('This workshop is completed.', '종료된 워크숍입니다.')}
              </div>
            )}

            <div>
              <div className='typo-b14Strong tabletH:typo-b18Strong text-green-deep04'>
                {label === 'Upcoming'
                  ? t(
                      `See you on the day of the workshop`,
                      '워크숍 당일에 만나요',
                    )
                  : t(`We're excited to see you`, '만나서 반가웠어요')}
              </div>
              <div className='typo-title1 tabletH:typo-h5 text-green-deep04'>
                {user.lastName}!
              </div>
            </div>
          </div>

          <div className='w-full flex flex-col items-start gap-1.5 tabletH:gap-2 py-4 tabletH:py-6 border-dashed border-t-[1.6px] border-t-grayscale-gray02'>
            <div className='typo-b14Strong text-text-primary'>
              {t('Details', '상세 정보')}
            </div>
            <div className='flex items-start gap-4'>
              <img
                src={data.imageUrl}
                alt={t('default profile', '기본 프로필')}
                className='size-[120px] min-w-[120px] min-h-[120px] rounded-lg object-cover'
              />
              <div className='flex flex-col justify-center items-start gap-3'>
                <div className='flex flex-col  items-start'>
                  <div className='flex gap-1.5'>
                    <Chip
                      type='fill'
                      state='disabled'
                      size='xs'
                      hideIcon
                      disableTypography
                      disableDefaultColors
                      bgclass={`h-auto rounded-md px-1.5 py-0.5 [&>span]:flex [&>span]:gap-1 ${labelTone.bg}`}
                      borderclass='ring-0'
                      textclass={`typo-b12Strong ${labelTone.text}`}
                    >
                      {t(label, LABEL_KR[label])}
                      {label === 'Upcoming' && (
                        <span>{formatDDay(data.date)}</span>
                      )}
                    </Chip>
                  </div>

                  <div className='typo-b16Strong tabletH:typo-b18Strong text-text-primary'>
                    {data.workshopTitle}
                  </div>
                  <div className='typo-krCaption tabletH:typo-b13 text-grayscale-gray07'>
                    {data.meetingPointAddress}
                  </div>
                </div>

                <div className='flex flex-col items-start text-text-primary'>
                  <div className='typo-b12Strong'>{t('Date', '날짜')}</div>
                  <div className='typo-b14Strong tabletH:typo-b16Strong'>
                    {formatDateToWeekdayString(data.date)} ∙{' '}
                    {formatTime(data.startTime)}
                    {' - '}
                    {formatTime(data.endTime)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='w-full flex flex-col items-start gap-1.5 tabletH:gap-2 py-4 tabletH:py-6 border-dashed border-t-[1.6px] border-t-grayscale-gray02'>
            <div className='typo-b14Strong text-text-primary'>
              {t('Booking number', '예약 번호')}
            </div>
            <div className='w-full flex justify-between items-center p-3 tabletH:p-4 bg-grayscale-gray00 rounded-lg'>
              <div className='typo-b16Strong tabletH:typo-b18Strong text-text-primary'>
                {data.bookingSerialNumber}
              </div>
              <button onClick={handleCopy} className='p-0.5'>
                <CopyIcon className='text-icon-primary hover:text-icon-secondary active:text-icon-primary' />
              </button>
            </div>
          </div>

          {label === 'Upcoming' && (
            <div className='flex flex-col items-start gap-4 pt-4 tabletH:pt-6 w-full border-dashed border-t-[1.6px] border-t-grayscale-gray02'>
              <BaseButton
                hideIcon
                type='text'
                size='l'
                width='100%'
                bgclass='bg-grayscale-gray00'
                onClick={() => openInfoModal('thingsToKnow')}
              >
                {t('Things to know', '알아두실 사항')}
              </BaseButton>
              <BaseButton
                hideIcon
                type='text'
                size='l'
                width='100%'
                bgclass='bg-grayscale-gray00'
                onClick={() => openInfoModal('refundPolicy')}
              >
                {t('Cancellation & refund policy', '취소 및 환불 정책')}
              </BaseButton>
            </div>
          )}
          {label === 'Attended' && <div className='pb-6 w-full h-[542px]' />}
        </div>

        {data?.receiptUrl && (
          <div className='w-full p-4 tabletH:px-[30px] tabletH:pt-4 tabletH:pb-6 flex justify-center items-start gap-2 border-t border-t-grayscale-gray02 bg-grayscale-gray01'>
            <BaseButton
              icon={<DownloadIcon />}
              size={isMobile ? 'l' : 'xl'}
              iconPosition='after'
              width='100%'
              onClick={handleDownloadReceipt}
            >
              {t('Download Receipt', '영수증 다운로드')}
            </BaseButton>
          </div>
        )}
      </div>

      <RefundModal
        isOpen={infoModal === 'thingsToKnow'}
        onClose={() => setInfoModal(null)}
        title={t('Things to know', '알아두실 사항')}
        content={workshopInfo?.thingsToKnow || ''}
        emptyText={
          workshopInfo
            ? t(
                'No additional notes for this workshop.',
                '이 워크숍에 대한 추가 안내가 없습니다.',
              )
            : t('Loading...', '불러오는 중...')
        }
      />
      <RefundModal
        isOpen={infoModal === 'refundPolicy'}
        onClose={() => setInfoModal(null)}
        title={t('Cancellation & Refund Policy', '취소 및 환불 정책')}
        content={workshopInfo?.refundPolicy || ''}
      />

      {copyToast}
    </div>,
    document.body,
  );
};

export default BookingDetailModal;
